import React, { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRightIcon } from './navIcons'
import {
  Badge,
  Chev,
  Flyout,
  FlyoutLink,
  FlyoutTitle,
  Ico,
  Lbl,
  NavItemRoot,
  NavLink,
  Submenu,
  SubmenuItem,
  SubmenuLink,
} from './styles'

export interface ModuleSub {
  Sub_ModuleID: number
}

export interface ModuleEntry {
  ModuleId: number
  Sub: ModuleSub[]
}

export interface SubModuleData {
  name: string
  link: string
}

export interface NavData {
  name: string
  link: string
  subModules?: Record<number, SubModuleData>
}

interface Props {
  module: ModuleEntry
  data: NavData
  icon: ReactNode
  pathname: string
  isLocationHere: boolean
  isOpen: boolean
  collapsed?: boolean
  onToggle: () => void
  onSubItemSideEffect: (moduleNum: number, subModuleId: number) => void
  onCloseMenu?: () => void
  onExpand?: () => void
  /** Optional small badge (e.g. unread count) — currently unused. */
  badge?: ReactNode
}

const isSubLinkActive = (pathname: string, subLink?: string) =>
  !!subLink && (pathname === subLink || pathname.startsWith(subLink + '/'))

const NavItem: React.FC<Props> = ({
  module,
  data,
  icon,
  pathname,
  isLocationHere,
  isOpen,
  collapsed,
  onToggle,
  onSubItemSideEffect,
  onCloseMenu,
  onExpand,
  badge,
}) => {
  const hasSubs = module.Sub.length > 0

  /* Determine sub-active for collapsed indicator dot. */
  const hasSubActive =
    hasSubs &&
    module.Sub.some((s) => {
      const sd = data.subModules?.[s.Sub_ModuleID]
      return isSubLinkActive(pathname, sd?.link)
    })

  /* ── Leaf nav item (no submenu) ─────────────────────────── */
  if (!hasSubs) {
    return (
      <NavItemRoot $collapsed={collapsed}>
        <NavLink
          as={Link}
          to={data.link}
          onClick={(e: React.MouseEvent) => {
            if (collapsed && onExpand) {
              e.preventDefault()
              onExpand()
              return
            }
            onCloseMenu?.()
          }}
          $active={isLocationHere}
          $collapsed={collapsed}
          aria-label={collapsed ? data.name : undefined}
        >
          <Ico $active={isLocationHere}>{icon}</Ico>
          {!collapsed && <Lbl>{data.name}</Lbl>}
          {!collapsed && badge ? <Badge $active={isLocationHere}>{badge}</Badge> : null}
        </NavLink>
        {collapsed && (
          <Flyout $tip>
            <FlyoutTitle>{data.name}</FlyoutTitle>
          </Flyout>
        )}
      </NavItemRoot>
    )
  }

  /* ── Dropdown nav item ──────────────────────────────────── */
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (collapsed && onExpand) {
      onExpand()
      return
    }
    onToggle()
  }

  return (
    <NavItemRoot
      $collapsed={collapsed}
      $hasSubActive={hasSubActive}
      className={isOpen ? 'open' : undefined}
    >
      <NavLink
        href='#'
        onClick={handleClick}
        $active={isLocationHere}
        $collapsed={collapsed}
        $hasSubActive={hasSubActive}
        aria-expanded={isOpen}
        aria-label={collapsed ? data.name : undefined}
      >
        <Ico $active={isLocationHere}>{icon}</Ico>
        {!collapsed && <Lbl>{data.name}</Lbl>}
        {!collapsed && (
          <Chev $open={isOpen} aria-hidden>
            <ChevronRightIcon />
          </Chev>
        )}
      </NavLink>

      {!collapsed && (
        <Submenu $open={isOpen} role='menu'>
          {module.Sub.map((sub) => {
            const subData = data.subModules?.[sub.Sub_ModuleID]
            if (!subData) return null
            const subActive = isSubLinkActive(pathname, subData.link)
            return (
              <SubmenuItem key={sub.Sub_ModuleID}>
                <SubmenuLink
                  as={Link}
                  to={subData.link}
                  $active={subActive}
                  onClick={() => {
                    onSubItemSideEffect(module.ModuleId, sub.Sub_ModuleID)
                    if (onCloseMenu) onCloseMenu()
                  }}
                >
                  {subData.name}
                </SubmenuLink>
              </SubmenuItem>
            )
          })}
        </Submenu>
      )}

      {collapsed && (
        <Flyout>
          <FlyoutTitle>{data.name}</FlyoutTitle>
          {module.Sub.map((sub) => {
            const subData = data.subModules?.[sub.Sub_ModuleID]
            if (!subData) return null
            const subActive = isSubLinkActive(pathname, subData.link)
            return (
              <FlyoutLink
                as={Link}
                key={sub.Sub_ModuleID}
                to={subData.link}
                $active={subActive}
                onClick={() => {
                  onSubItemSideEffect(module.ModuleId, sub.Sub_ModuleID)
                  if (onCloseMenu) onCloseMenu()
                }}
              >
                {subData.name}
              </FlyoutLink>
            )
          })}
        </Flyout>
      )}
    </NavItemRoot>
  )
}

export default NavItem
